import {
  FIELDS,
  RepaymentStrategyField,
  RepaymentStrategy,
  Affordability,
  Approvals,
  Assets,
  Borrower,
  CreditRecord,
  Expenditure,
  Income,
  MortgageProperty,
  SpecialConditions,
  Summary,
  Loan,
} from 'modules/cm/domain/Underwriting/Underwriting';
import {
  AffordabilityFields,
  ApprovalsFields,
  BorrowerFields,
  CreditRecordFields,
  ExpenditureFields,
  IncomeFields,
  Labelled,
  LoanFields,
  MortgagePropertyFields,
  SummaryFields,
  getRepaymentStrategyFields,
} from 'modules/cm/form/Underwriting/fields';
import { Nullable } from 'utils/types';
import formatCurrencyValue from 'utils/formatCurrency';
import { Rows as ViewModelRows } from 'modules/cm/components/UWTable/UWTable.types';
import { mapToViewModelRows } from 'modules/cm/mapper/Underwriting/viewModelMapper';
import { ProductType } from 'domain/Product';

export const makeLabel = (field: Labelled, nested: boolean = false): string => {
  return `${nested ? ' ' : ''}${field.label}`;
};

export const makeValue = (value: any): string => {
  return value || '-';
};

export const makeBooleanValue = (value: Nullable<boolean>): string => {
  return value ? 'Yes' : 'No';
};

const makeCurrency = (value: any): string => {
  return value ? formatCurrencyValue(value) : makeValue(value);
};

export const mapBorrowerToViewModel = (data: Borrower): ViewModelRows => {
  return mapToViewModelRows<Borrower['applicant1']>(
    [
      { field: FIELDS.LAST_NAME },
      { field: FIELDS.FIRST_NAME },
      { field: FIELDS.GENDER },
      { field: FIELDS.AGE },
      { field: FIELDS.NATIONALITY },
      { field: FIELDS.MARITAL_STATUS },
      { field: FIELDS.POWER_OF_ATTORNEY_FULL_NAME },
      { field: FIELDS.EMPLOYMENT_STATUS },
      { field: FIELDS.PLANNED_RETIREMENT_AGE },
      { field: FIELDS.PROOF_OF_ID },
      { field: FIELDS.PROOF_OF_ADDRESS },
    ],
    [data.applicant1, data.applicant2],
    BorrowerFields['applicant1'],
  );
};

export const mapMortgagePropertyToViewModel = (data: MortgageProperty): ViewModelRows => {
  return mapToViewModelRows<MortgageProperty>(
    [
      { field: FIELDS.FLAT_NUMBER },
      { field: FIELDS.BUILDING_NUMBER },
      { field: FIELDS.BUILDING_NAME },
      { field: FIELDS.STREET },
      { field: FIELDS.CITY },
      { field: FIELDS.REGION },
      { field: FIELDS.POSTCODE },
      { field: FIELDS.TYPE },
      { field: FIELDS.TENURE },
      { field: FIELDS.REMAINING_LEASE_YEARS },
      { field: FIELDS.VALUATION_ESTIMATED_VALUE },
      { field: FIELDS.VALUATION_CONFIRMED_VALUE },
      { field: FIELDS.VALUATION_INSPECTION_DATE },
      { field: FIELDS.VALUATION_TYPE },
    ],
    [data],
    MortgagePropertyFields,
  );
};

export const mapCreditRecordCommonDataToViewModel = (data: CreditRecord): ViewModelRows => {
  return [
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: makeLabel(CreditRecordFields[FIELDS.DELPHI_SCORE]) },
        { htmlClass: 'bold', value: makeValue(data[FIELDS.DELPHI_SCORE]) },
      ],
    },
  ];
};

export const mapCreditRecordToViewModel = (data: CreditRecord['applicant1' | 'applicant2']): ViewModelRows => {
  const applicantsCreditRecord = data as CreditRecord['applicant1'];
  return [
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: 'CCJ' },
        { htmlClass: 'bold', value: makeCurrency(applicantsCreditRecord[FIELDS.CCJ_AMOUNT]) },
        { htmlClass: 'bold', value: makeValue(applicantsCreditRecord[FIELDS.CCJ_LATEST_DATE]) },
      ],
    },
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: 'Default' },
        { htmlClass: 'bold', value: makeCurrency(applicantsCreditRecord[FIELDS.DEFAULT_AMOUNT]) },
        { htmlClass: 'bold', value: makeValue(applicantsCreditRecord[FIELDS.DEFAULT_LATEST_DATE]) },
      ],
    },
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: 'Mortgage Arrears' },
        { htmlClass: 'bold', value: makeCurrency(applicantsCreditRecord[FIELDS.MORTGAGE_ARREARS_AMOUNT]) },
        { htmlClass: 'bold', value: makeValue(applicantsCreditRecord[FIELDS.MORTGAGE_ARREARS_LATEST_DATE]) },
      ],
    },
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: 'Bankruptcy Proceedings' },
        { htmlClass: 'bold', value: makeCurrency(applicantsCreditRecord[FIELDS.BANKRUPTCY_AMOUNT]) },
        { htmlClass: 'bold', value: makeValue(applicantsCreditRecord[FIELDS.BANKRUPTCY_LATEST_DATE]) },
      ],
    },
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: 'Criminal convictions details' },
        { htmlClass: 'bold', value: makeBooleanValue(applicantsCreditRecord[FIELDS.HAS_CRIMINAL_CONVICTIONS]) },
      ],
    },
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: '' },
        { htmlClass: 'bold', value: makeValue(applicantsCreditRecord[FIELDS.CRIMINAL_CONVICTIONS_DETAILS]) },
      ],
    },
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: 'Adverse credit details' },
        { htmlClass: 'bold', value: makeBooleanValue(applicantsCreditRecord[FIELDS.HAS_ADVERSE_CREDIT]) },
      ],
    },
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: '' },
        { htmlClass: 'bold', value: makeValue(applicantsCreditRecord[FIELDS.ADVERSE_CREDIT_DETAILS]) },
      ],
    },
  ];
};

export const mapCurrentIncomeToViewModel = (data: Income['current']): ViewModelRows => {
  const res = mapToViewModelRows<Income['current']['applicant1']>(
    [
      { field: FIELDS.EMPLOYMENT_INCOME },
      { field: FIELDS.SELF_EMPLOYMENT_INCOME },
      { field: FIELDS.OTHER_INCOME },
      { field: FIELDS.PENSION_INCOME },
      { htmlClass: 'borderTop', field: FIELDS.TOTAL_INCOME },
    ],
    [data.applicant1, data.applicant2],
    IncomeFields['current'],
  );
  res.push({
    htmlClass: null,
    cells: [
      { htmlClass: 'bold', value: makeLabel(IncomeFields['current'][FIELDS.COMBINED_INCOME]) },
      { htmlClass: 'bold', value: makeCurrency(data[FIELDS.COMBINED_INCOME]) },
    ],
  });
  return res;
};

export const mapFutureIncomeToViewModel = (data: Income['future']): ViewModelRows => {
  const res = mapToViewModelRows<Income['future']['applicant1']>(
    [
      { field: FIELDS.EMPLOYMENT_INCOME },
      { field: FIELDS.SELF_EMPLOYMENT_INCOME },
      { field: FIELDS.OTHER_INCOME },
      { field: FIELDS.PENSION_INCOME },
      { htmlClass: 'borderTop', field: FIELDS.TOTAL_INCOME },
    ],
    [data.applicant1, data.applicant2],
    IncomeFields['future'],
  );
  res.push({
    htmlClass: null,
    cells: [
      { htmlClass: 'bold', value: makeLabel(IncomeFields['future'][FIELDS.COMBINED_INCOME]) },
      { htmlClass: 'bold', value: makeCurrency(data[FIELDS.COMBINED_INCOME]) },
    ],
  });
  return res;
};

export const mapSurvivorIncomeToViewModel = (data: Income['survivor']): ViewModelRows => {
  return mapToViewModelRows<Income[FIELDS.SURVIVOR][FIELDS.APPLICANT_1]>(
    [{ htmlClass: null, field: FIELDS.SURVIVOR_INCOME }],
    [data.applicant1, data.applicant2],
    IncomeFields[FIELDS.SURVIVOR],
  );
};

export const mapAssetsToViewModel = (data: Assets['applicant1' | 'applicant2']): ViewModelRows => {
  return (data as Assets['applicant1']).map((asset) => ({
    htmlClass: null,
    cells: [
      { htmlClass: 'normal', value: makeValue(asset.type) },
      { htmlClass: 'bold', value: makeCurrency(asset.value) },
      { htmlClass: 'bold', value: makeValue(asset.description) },
    ],
  }));
};

export const mapExpenditureToViewModels = (data: Expenditure): ViewModelRows[] => {
  return [data.applicant1, data.applicant2]
    .filter((_) => _)
    .map((applicantsExpenditure) => {
      const expenditure = applicantsExpenditure as Expenditure['applicant1'];
      return mapToViewModelRows<Expenditure['applicant1']['monthly']>(
        [
          { htmlClass: null, field: FIELDS.COMMITMENT_TO_BE_CLEARED },
          { htmlClass: null, field: FIELDS.COMMITMENT_REMAINING },
          { htmlClass: 'borderTop', field: FIELDS.COMMITMENT_TOTAL },
        ],
        [expenditure.monthly, expenditure.balance],
        ExpenditureFields['applicant1']['monthly'],
      );
    });
};
export const mapExpenditureCommonDataToViewModels = (data: Expenditure): ViewModelRows => {
  return [
    {
      htmlClass: null,
      cells: [
        { htmlClass: 'normal', value: makeLabel(ExpenditureFields[FIELDS.COMMITMENT_COMBINED_MONTHLY_BASIC_SPEND]) },
        { htmlClass: 'bold', value: makeCurrency(data[FIELDS.COMMITMENT_COMBINED_MONTHLY_BASIC_SPEND]) },
      ],
    },
  ];
};

export const mapAffordabilityToViewModel = (data: Affordability): ViewModelRows => {
  return mapToViewModelRows<Affordability>(
    [
      { field: FIELDS.MAXIMUM_LOAN_POSSIBLE },
      { field: FIELDS.DISPOSABLE_INCOME },
      { field: FIELDS.DISPOSABLE_INCOME_BUFFER },
      { field: FIELDS.STAGE },
      { field: FIELDS.LIVING_COSTS },
      { field: FIELDS.NET_CASHFLOW },
    ],
    [data],
    AffordabilityFields,
  );
};

export const mapSpecialConditionsToViewModel = (data: SpecialConditions): ViewModelRows => {
  return [{ htmlClass: null, cells: [{ htmlClass: 'normal', value: data[FIELDS.SPECIAL_CONDITIONS] || 'N/A' }] }];
};

export const mapApprovalsToViewModel = (data: Approvals): ViewModelRows => {
  return mapToViewModelRows<Approvals>(
    [
      { field: FIELDS.INITIAL_ASSESSMENT_BY },
      { field: FIELDS.INITIAL_ASSESSMENT_DATE },
      { field: FIELDS.APPROVED_BY },
      { field: FIELDS.APPROVED_DATE },
    ],
    [data],
    ApprovalsFields,
  );
};

export const mapSummaryToViewModel = (data: Summary): ViewModelRows => {
  return mapToViewModelRows(
    [
      { field: FIELDS.APPLICATION_ID },
      { field: FIELDS.APPLICANT_SURNAME },
      { field: FIELDS.APPLICATION_DATE },
      { field: FIELDS.UNDERWRITER },
      { field: FIELDS.FIRM_NAME },
      { field: FIELDS.DELEGATED_AUTHORITY_LEVEL },
      { field: FIELDS.HAS_ADVERSE_CREDIT },
      { field: FIELDS.IS_CREDIT_POLICY_EXEMPTION },
      { field: FIELDS.CREDIT_POLICY_EXCEPTION_RATIONALE },
      { field: FIELDS.UNDERWRITER_RECOMMENDATION },
    ],
    [data],
    SummaryFields,
  );
};

export const mapLoanToViewModel = (data: Omit<Loan, FIELDS.PRODUCT_TYPE>): ViewModelRows => {
  return mapToViewModelRows(
    [
      { field: FIELDS.PRODUCT_NAME },
      { field: FIELDS.FIXED_INTEREST_RATE },
      { field: FIELDS.PRODUCT_TERM_LENGTH },
      { field: FIELDS.LOAN_PURPOSE },
      { field: FIELDS.LOAN_PURCHASE },
      { field: FIELDS.LOAN_REMORTGAGE },
      { field: FIELDS.DEBT_CONSOLIDATION },
      { field: FIELDS.HOME_IMPROVEMENTS },
      { field: FIELDS.DISCRETIONARY_SPEND },
      { field: FIELDS.ASSISTING_OTHERS },
      { field: FIELDS.OTHER_PURPOSES },
      { field: FIELDS.NET_LOAN },
      { field: FIELDS.GROSS_LOAN },
      { field: FIELDS.LOAN_TO_VALUE },
      { field: FIELDS.DEBT_TO_INCOME },
    ],
    [data],
    LoanFields,
  );
};

export const mapRepaymentStrategyToViewModel = ({
  productType,
  ...data
}: {
  productType: ProductType;
} & RepaymentStrategy): ViewModelRows => {
  return mapToViewModelRows(
    [
      { field: RepaymentStrategyField.HaveSaleOfPropertyAndDownsizing },
      { field: RepaymentStrategyField.HaveSaleOfAnotherProperty },
      { field: RepaymentStrategyField.HaveInvestments },
      { field: RepaymentStrategyField.HaveEndownments },
      { field: RepaymentStrategyField.HaveOther },
    ],
    [data],
    getRepaymentStrategyFields(productType),
  );
};
